import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Tooltip,
} from '@mui/material';
import { relativeTime } from '../../notifications/notifUtils';
import useAutoFocus from '../../../hooks/useAutoFocus';
import './review.css';

// Absolute, human-readable timestamp for the hover card (the header keeps the
// compact relative time). Falls back to the raw value if it isn't parseable.
function absoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}
import CommentComposer from './CommentComposer';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

// OPEN amber / ADDRESSED accent / CLOSED neutral / WONT_FIX neutral (spec
// §Comments) — rendered via ds.css's `.badge` classes (docs/design/specimen/
// review.html uses the same OPEN/ADDRESSED/CLOSED badge language, #450).
const STATUS_CHIP = {
  open: { label: 'OPEN', badgeClass: 'warning' },
  addressed: { label: 'ADDRESSED', badgeClass: 'accent' },
  closed: { label: 'CLOSED', badgeClass: 'neutral' },
  wont_fix: { label: "WON'T FIX", badgeClass: 'neutral' },
};

// CR impact classes offered by the escalate dialog (mirrors the REST
// EscalateBody Literal + raise_change_request cr_class).
const CR_CLASSES = [
  ['local_fix', 'Local fix'],
  ['reopens_requirement', 'Reopens requirement'],
  ['reopens_architecture', 'Reopens architecture'],
  ['significant_redesign', 'Significant redesign'],
];

function initialsOf(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// The anchor is stored as a JSON string (or object); render a compact label.
function anchorLabel(anchor) {
  if (!anchor) return null;
  let a = anchor;
  if (typeof a === 'string') {
    try { a = JSON.parse(a); } catch { return null; }
  }
  if (a && typeof a === 'object' && a.field) return `on: ${a.field}`;
  return 'anchored';
}

function CommentCard({
  comment, isReply = false, currentUserId = null, onAction, onReplyClick = null,
  onEscalateClick = null, onResolveClick = null, onDeleteClick = null,
  // #651: the comment "Edit" affordance. `hasReplies` is computed by the parent
  // CommentThread (it already knows the full flat comment list; a single
  // top-level comment has no way to know this about itself) — edit is offered
  // only on the raiser's own OPEN, UNREPLIED, top-level comment, mirroring the
  // Delete affordance's own scope one section down. `isEditing`/`onEditClick`/
  // `onEditSave`/`onEditCancel` are the inline-edit state machine, also owned
  // by the parent (same pattern as `replyingTo`/`escalating`/`noting` below).
  hasReplies = false, onEditClick = null, isEditing = false,
  onEditSave = null, onEditCancel = null,
}) {
  const chip = STATUS_CHIP[comment.status] || STATUS_CHIP.open;
  const isRaiser = !!currentUserId && comment.raised_by === currentUserId;
  const anchor = anchorLabel(comment.anchor);
  const isOpen = comment.status === 'open';
  const canEdit = !isReply && isRaiser && isOpen && !hasReplies && !!onEditClick;
  // Prefer the enriched author identity (#358): display name → email → raw id.
  // The raw principal id (a GUID) is the last resort so a comment never shows a
  // blank name, but a human identity wins whenever the backend could resolve one.
  const authorEmail = comment.raised_by_email || null;
  const authorName = comment.raised_by_name || authorEmail || comment.raised_by;
  // Escalate is offered on an open, top-level, not-yet-escalated comment.
  const canEscalate = !isReply && !comment.escalated_cr_id && isOpen && !!onEscalateClick;
  // Mock's `.cmt`/`.reply` (docs/design/specimen/review.html, #450): a reply is a
  // visually distinct nested block (`.reply`, sunken bg, indented), not just a
  // margin-shifted copy of the same `.cmt` card.
  return (
    <div
      role="listitem"
      id={`comment-${comment.comment_id}`}
      data-testid={`comment-${comment.comment_id}`}
      data-status={comment.status}
      className={isReply ? 'reply' : `cmt ${comment.status}`}
    >
      <div className="cmt-h">
        {/* Author display name + initials come from the enriched thread
            (raised_by_name/raised_by_email, #358); fall back to the raw id when
            the principal has no resolvable user row (e.g. an agent identity). */}
        <Tooltip
          arrow
          title={(
            <Box data-testid={`comment-author-tip-${comment.comment_id}`}>
              <Box sx={{ fontWeight: 600 }}>{authorName}</Box>
              {authorEmail && (
                <Box>
                  <a href={`mailto:${authorEmail}`} style={{ color: 'inherit' }}
                     data-testid={`comment-author-email-${comment.comment_id}`}>
                    {authorEmail}
                  </a>
                </Box>
              )}
              <Box sx={{ opacity: 0.85 }}>{absoluteTime(comment.raised_at)}</Box>
            </Box>
          )}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <span className="avatar">{initialsOf(authorName)}</span>
            <span className="who">{authorName}</span>
          </span>
        </Tooltip>
        <span className="when">{relativeTime(comment.raised_at)}</span>
        <div className="grow" />
        {anchor && (
          <span className="rmdv-typechip" data-testid={`comment-anchor-${comment.comment_id}`}>
            {anchor}
          </span>
        )}
        {/* kind='overall' is a normal comment on the item — this tag signals
            provenance (a review-pass summary), not a different visibility rule
            (spec §Comments "Overall = a comment on the item, full stop"). Styled
            as the mock's `.typechip` "Review summary" tag (#450). */}
        {comment.kind === 'overall' && (
          <span className="rmdv-typechip">Review summary</span>
        )}
        {/* Status badge — ds.css's `.badge` classes, same OPEN/ADDRESSED/CLOSED
            language as the mock (docs/design/specimen/review.html, #450). */}
        <span className={`badge ${chip.badgeClass}`}>{chip.label}</span>
      </div>
      {isEditing ? (
        <Box sx={{ mt: 0.5, mb: 0.5 }}>
          <CommentComposer
            initialValue={comment.body}
            placeholder="Edit comment — plain text."
            testId={`comment-edit-composer-${comment.comment_id}`}
            onCancel={onEditCancel}
            onSave={onEditSave}
          />
        </Box>
      ) : (
        <div className="cmt-body">{comment.body}</div>
      )}
      {comment.resolution && (
        <div className="cmt-res">
          <b>{comment.addressed_by || 'Agent'}</b>
          {comment.addressed_at ? ` · ${relativeTime(comment.addressed_at)}` : ''}
          {' · '}
          {comment.resolution}
        </div>
      )}
      {comment.escalated_cr_id && (
        <div className="cmt-act" data-testid={`comment-escalated-${comment.comment_id}`}>
          <span className="badge accent">{`Escalated → ${comment.escalated_cr_id}`}</span>
        </div>
      )}
      {/* Actions: Reply (any authenticated user, top-level only — V1 threading
          is one level deep), raiser-only Edit/Close/Reopen, and Escalate-to-CR.
          The UI hiding a button is not the real control (the server enforces
          it too), but keeps the affordance honest (spec §Comments / Security).
          Rendered as `.linkbtn` (mock's action style), not filled MUI buttons.
          Hidden entirely while editing — the inline composer above owns Save/
          Cancel for that state; the rest of the action set stays put once it
          closes. */}
      {!isEditing && (
      <div className="cmt-act">
        {!isReply && onReplyClick && (
          <button type="button" className="linkbtn" onClick={() => onReplyClick(comment.comment_id)}
                  data-testid={`reply-${comment.comment_id}`}>Reply</button>
        )}
        {/* #651: raiser-only, open, unreplied, top-level — see `canEdit` above.
            Backend enforces raiser+open on PATCH .../edit; "unreplied" is a UI-only
            narrowing (the backend doesn't gate edit on replies, only delete) so a
            reply someone else already wrote never appears to reference text the
            raiser then silently changed out from under it. */}
        {canEdit && (
          <button type="button" className="linkbtn" onClick={() => onEditClick(comment.comment_id)}
                  data-testid={`edit-${comment.comment_id}`}>Edit</button>
        )}
        {/* Manual status changes on an OPEN comment (spec §Comments: "anyone with
            edit can Mark addressed / wont_fix with a note" — normally the agent
            does this via MCP, but a human must be able to as well). */}
        {isOpen && onResolveClick && (
          <button type="button" className="linkbtn" onClick={() => onResolveClick(comment.comment_id, 'resolve')}
                  data-testid={`resolve-${comment.comment_id}`}>Mark addressed</button>
        )}
        {isOpen && onResolveClick && (
          <button type="button" className="linkbtn muted" onClick={() => onResolveClick(comment.comment_id, 'wont_fix')}
                  data-testid={`wontfix-${comment.comment_id}`}>Won&apos;t fix</button>
        )}
        {canEscalate && (
          <button type="button" className="linkbtn" onClick={() => onEscalateClick(comment.comment_id)}
                  data-testid={`escalate-${comment.comment_id}`}>Escalate to CR</button>
        )}
        {!isReply && isRaiser && comment.status === 'addressed' && (
          <>
            <button type="button" className="linkbtn" onClick={() => onAction(comment.comment_id, 'close')}>Close</button>
            <button type="button" className="linkbtn muted" onClick={() => onAction(comment.comment_id, 'reopen')}>Reopen</button>
          </>
        )}
        {!isReply && isRaiser && (comment.status === 'closed' || comment.status === 'wont_fix') && (
          <button type="button" className="linkbtn muted" onClick={() => onAction(comment.comment_id, 'reopen')}>Reopen</button>
        )}
        {/* Delete: raiser-only, open + unreplied (the backend enforces it too). */}
        {isOpen && isRaiser && onDeleteClick && (
          <button type="button" className="linkbtn danger" onClick={() => onDeleteClick(comment.comment_id)}
                  data-testid={`delete-${comment.comment_id}`}>Delete</button>
        )}
      </div>
      )}
    </div>
  );
}

CommentCard.propTypes = {
  comment: PropTypes.shape({
    comment_id: PropTypes.string.isRequired,
    parent_comment_id: PropTypes.string,
    kind: PropTypes.string,
    body: PropTypes.string,
    status: PropTypes.string,
    raised_by: PropTypes.string,
    raised_by_name: PropTypes.string,
    raised_by_email: PropTypes.string,
    raised_at: PropTypes.string,
    resolution: PropTypes.string,
    addressed_by: PropTypes.string,
    addressed_at: PropTypes.string,
    anchor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    escalated_cr_id: PropTypes.string,
  }).isRequired,
  isReply: PropTypes.bool,
  currentUserId: PropTypes.string,
  onAction: PropTypes.func.isRequired,
  onReplyClick: PropTypes.func,
  onEscalateClick: PropTypes.func,
  onResolveClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  hasReplies: PropTypes.bool,
  onEditClick: PropTypes.func,
  isEditing: PropTypes.bool,
  onEditSave: PropTypes.func,
  onEditCancel: PropTypes.func,
};


function ResolveNoteDialog({ open, action, onClose, onSubmit }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // #793: rendered unconditionally with `open` toggling — a bare `autoFocus`
  // only fires on the FIRST mount, so a reopen after the first silently lost
  // focus. See hooks/useAutoFocus.
  const focusRef = useAutoFocus(open);
  const isWontFix = action === 'wont_fix';
  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await onSubmit(note.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="resolve-dialog">
      <DialogTitle>{isWontFix ? "Mark won't fix" : 'Mark addressed'}</DialogTitle>
      <DialogContent>
        <TextField
          inputRef={focusRef} fullWidth margin="dense" multiline minRows={2}
          label={isWontFix ? 'Reason (why this won’t be actioned)' : 'Resolution (what was done)'}
          value={note} onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'resolve-note' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !note.trim()}
                data-testid="resolve-submit">
          {isWontFix ? "Won't fix" : 'Mark addressed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ResolveNoteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  action: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

function EscalateDialog({ open, onClose, onSubmit }) {
  const [crClass, setCrClass] = useState('reopens_requirement');
  const [cause, setCause] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!cause.trim() || !reason.trim()) return;
    setBusy(true);
    try {
      await onSubmit({ crClass, cause: cause.trim(), reason: reason.trim() });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="escalate-dialog">
      <DialogTitle>Escalate comment to change request</DialogTitle>
      <DialogContent>
        <TextField
          select fullWidth margin="dense" label="Impact class" value={crClass}
          onChange={(e) => setCrClass(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'escalate-cr-class' } }}
        >
          {CR_CLASSES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
        <TextField
          fullWidth margin="dense" label="Cause (what the review found)" value={cause}
          onChange={(e) => setCause(e.target.value)} multiline minRows={2}
          slotProps={{ htmlInput: { 'data-testid': 'escalate-cause' } }}
        />
        <TextField
          fullWidth margin="dense" label="Reason (audit note)" value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'escalate-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" onClick={submit} disabled={busy || !cause.trim() || !reason.trim()}
          data-testid="escalate-submit"
        >
          Raise CR
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EscalateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * A single item's (or the root's) comment thread — collapsed/expanded by the
 * PARENT (ItemBlock owns the expand toggle so the count badge can drive it).
 * `role="list"` per spec Accessibility; body text is rendered as plain React
 * text nodes (`{comment.body}`) — NEVER `dangerouslySetInnerHTML` — so any
 * HTML/script in a comment body renders inert (Security §1).
 *
 * Phase 3: a Reply affordance opens a composer that posts with
 * `parent_comment_id` (one level deep — replies get no further Reply button);
 * an Escalate action raises a change_request from the comment.
 */
function CommentThread({
  comments = EMPTY_ARRAY, currentUserId = null, onAction, onReply = null, onEscalate = null,
  onDelete = null,
}) {
  const [replyingTo, setReplyingTo] = useState(null);
  const [escalating, setEscalating] = useState(null);
  const [noting, setNoting] = useState(null); // { commentId, action } | null
  // #651: which top-level comment (if any) is being inline-edited right now.
  const [editingId, setEditingId] = useState(null);
  const topLevel = (comments || []).filter((c) => c && !c.parent_comment_id);
  const repliesOf = (id) => (comments || []).filter((c) => c && c.parent_comment_id === id);

  return (
    <div role="list" aria-label="Comments" className="thread" data-testid="comment-thread">
      {topLevel.map((c) => (
        <React.Fragment key={c.comment_id}>
          <CommentCard
            comment={c}
            currentUserId={currentUserId}
            onAction={onAction}
            onReplyClick={onReply ? setReplyingTo : null}
            onEscalateClick={onEscalate ? setEscalating : null}
            onResolveClick={(id, action) => setNoting({ commentId: id, action })}
            onDeleteClick={onDelete ? (id) => onDelete(id) : null}
            hasReplies={repliesOf(c.comment_id).length > 0}
            onEditClick={setEditingId}
            isEditing={editingId === c.comment_id}
            onEditSave={async (body) => {
              await onAction(c.comment_id, 'edit', { body });
              setEditingId(null);
            }}
            onEditCancel={() => setEditingId(null)}
          />
          {repliesOf(c.comment_id).map((r) => (
            <CommentCard key={r.comment_id} comment={r} isReply currentUserId={currentUserId} onAction={onAction} />
          ))}
          {onReply && replyingTo === c.comment_id && (
            <Box sx={{ ml: 3 }}>
              <CommentComposer
                placeholder="Reply — plain text."
                onCancel={() => setReplyingTo(null)}
                onSave={async (body) => {
                  await onReply(c.comment_id, body);
                  setReplyingTo(null);
                }}
              />
            </Box>
          )}
        </React.Fragment>
      ))}
      {onEscalate && (
        <EscalateDialog
          open={!!escalating}
          onClose={() => setEscalating(null)}
          onSubmit={async (fields) => {
            await onEscalate(escalating, fields);
            setEscalating(null);
          }}
        />
      )}
      <ResolveNoteDialog
        open={!!noting}
        action={noting && noting.action}
        onClose={() => setNoting(null)}
        onSubmit={async (note) => {
          await onAction(noting.commentId, noting.action, { resolution: note });
          setNoting(null);
        }}
      />
    </div>
  );
}

CommentThread.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.string,
  onAction: PropTypes.func.isRequired,
  onReply: PropTypes.func,
  onEscalate: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CommentThread;
